/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { DefaultOptions, MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { Time } from './shared';
import KeycloakService from '../../services/KeycloakService';
import { captureRecoverableState } from '../../services/sessionRecovery';

const isDev = process.env.NEXT_PUBLIC_ENV === 'local';

// Helper function to check if error is an *authentication* failure — i.e. the
// session itself is dead and re-login can fix it. Keyed off the server's
// `code: 'UNAUTHENTICATED'` (set in server.ts's keycloak.accessDenied
// override), not the HTTP status: a bare 403 is ambiguous — it can mean
// authenticated-but-wrong-role, tenant/resource authorization (not a member
// of the team that owns this data), or license gating, none of which doLogin()
// can fix (Keycloak just hands back the same valid token and the request
// 403s again). Treating any 403 as an auth failure caused an infinite
// redirect loop (doLogin -> remount -> refetch -> 403 -> doLogin -> ...) on
// any endpoint enforcing tenant/resource authorization. 401 status is kept as
// a fallback in case a response ever reaches here without the code (e.g. from
// a proxy/gateway in front of this API), but `code` is the primary contract.
const isAuthError = (error: any): boolean => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  if (code) return code === 'UNAUTHENTICATED';
  return status === 401;
};

// Flag to prevent multiple simultaneous auth error handlers
let isHandlingAuthError = false;

const handleAuthError = async (error: any) => {
  console.error('ERROR On auth:', JSON.stringify(error, null, 2));
  console.error('[AUTH] Authentication error detected:', {
    status: error?.response?.status,
    statusText: error?.response?.statusText,
    message: error?.message,
    errorObj: error,
  });

  if (isHandlingAuthError) {
    console.warn('[AUTH] Auth error already being handled, skipping...');
    return;
  }

  isHandlingAuthError = true;

  try {
    console.warn('[AUTH] Token expired, redirecting to warnin...');
    // This redirect wipes all React state -- snapshot whatever the current
    // page has registered via useDraftRecovery() so it can hand it back once
    // the user is back and re-authenticated.
    captureRecoverableState();
    KeycloakService.doLogin();
  } finally {
    setTimeout(() => {
      isHandlingAuthError = false;
    }, 1000);
  }
};

const defaultQueryOptions: DefaultOptions = {
  queries: {
    staleTime: 10 * Time.MINUTE,
    gcTime: 20 * Time.MINUTE,
    retry: (failureCount, error: any) => {
      console.warn('[RETRY] Checking if should retry:', {
        failureCount: failureCount,
        status: error?.response?.status,
        message: error?.message,
      });

      if (isAuthError(error)) {
        console.warn('[RETRY] Auth error detected, not retrying, will handle auth');
        handleAuthError(error);
        return false;
      }
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        console.warn('[RETRY] 4xx error, not retrying');
        return false;
      }
      const shouldRetry = failureCount < 3;
      console.warn('[RETRY] Decision:', shouldRetry ? 'Retry' : 'Stop');
      return shouldRetry;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: !isDev,
    refetchOnReconnect: true,
    refetchOnMount: true,
  },
  mutations: {
    retry: 0,
    retryDelay: 1000,
  },
};

// Global error handler for queries
const queryCache = new QueryCache({
  onError: (error: any, query) => {
    console.error('[QUERY CACHE] Error caught:', {
      message: error?.message,
      status: error?.response?.status,
      queryKey: query.queryKey,
      error: error,
    });

    if (isAuthError(error)) {
      console.error('[QUERY CACHE] Detected auth error, handling...');
      handleAuthError(error);
      return;
    }

    if (error?.response?.status >= 500) {
      console.error('[QUERY CACHE] Server error:', {
        error: error.message,
        status: error?.response?.status,
        url: error?.config?.url,
        queryKey: query.queryKey,
      });
    }
  },
});

// Global error handler for mutations
const mutationCache = new MutationCache({
  onError: (error: any, variables, context, mutation) => {
    console.error('[MUTATION CACHE] Error caught:', {
      message: error?.message,
      status: error?.response?.status,
      mutationKey: mutation.options.mutationKey,
      error: error,
    });

    if (isAuthError(error)) {
      console.error('[MUTATION CACHE] Detected auth error, handling...');
      handleAuthError(error);
      return;
    }

    console.error('[MUTATION CACHE] Error:', {
      error: error.message,
      status: error?.response?.status,
      variables: variables,
      mutationKey: mutation.options.mutationKey,
    });
  },
});

// Create the query client with global configuration
export const queryClient = new QueryClient({
  defaultOptions: defaultQueryOptions,
  queryCache: queryCache,
  mutationCache: mutationCache,
});

// Cache invalidation utilities
export const invalidateQueries = {
  // Invalidate all queries
  all: () => queryClient.invalidateQueries(),

  // Invalidate queries by key pattern
  byKey: (queryKey: string[]) => queryClient.invalidateQueries({ queryKey: queryKey }),

  // Invalidate queries that match a predicate
  byPredicate: (predicate: (query: any) => boolean) =>
    queryClient.invalidateQueries({ predicate: predicate }),

  // Common invalidation patterns
  auth: () => queryClient.invalidateQueries({ queryKey: ['auth'] }),
  user: () => queryClient.invalidateQueries({ queryKey: ['user'] }),
  announcements: () => queryClient.invalidateQueries({ queryKey: ['announcements'] }),
};

// Cache management utilities
export const cacheUtils = {
  // Clear all cache
  clear: () => queryClient.clear(),

  // Remove specific queries from cache
  removeQueries: (queryKey: string[]) => queryClient.removeQueries({ queryKey: queryKey }),

  // Get cached data
  getQueryData: <T>(queryKey: string[]) => queryClient.getQueryData<T>(queryKey),

  // Set cached data
  setQueryData: <T>(queryKey: string[], data: T) => queryClient.setQueryData(queryKey, data),

  // Prefetch data
  prefetchQuery: (options: any) => queryClient.prefetchQuery(options),
};

export default queryClient;
